import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Col, Form, Offcanvas, type OffcanvasProps, Row, Stack } from "react-bootstrap";

import classNames from "classnames";
import dayjs from "dayjs";
import { isEmpty, isNil, toNumber } from "lodash";

import {
  AcquiredDateChoices,
  type ArtistResaleInput,
  type ArtworkInput,
  ListAllNotesDocument,
  type Resale,
  type ResaleDataInput,
  type ResaleFragment,
  type ResaleInput,
  ResaleStatuses,
  useInsertOrUpdateResaleNoteMutation,
  useInsertOrUpdateResalesBatchMutation,
} from "@api/graphql";
import { ToastMessage, type ToastMessageType } from "@components/ToastMessage";
import { ArtistDetailsDrawer } from "@components/detail_drawer/ArtistDetailsDrawer";
import { ArtworkDetailsDrawer } from "@components/detail_drawer/ArtworkDetailsDrawer";
import { DetailsNote } from "@components/detail_drawer/DetailsNote";
import { PaymentBadgeDrawer } from "@components/detail_drawer/PaymentBadgeDrawer";
import { PaymentTrackerDrawer } from "@components/detail_drawer/PaymentTrackerDrawer";
import { RecipientsBreakdown } from "@components/detail_drawer/RecipientsBreakdown";
import { ReportingDetailsDrawer } from "@components/detail_drawer/ReportingDetailsDrawer";
import { ResaleDetailsDrawer } from "@components/detail_drawer/ResaleDetailsDrawer";
import { ResalePriceBreakdown } from "@components/detail_drawer/ResalePriceBreakdown";
import { ResaleStatus } from "@components/detail_drawer/ResaleStatus";
import { ResaleStatusHistoryDrawer } from "@components/detail_drawer/ResaleStatusHistoryDrawer";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { ArtistDetails } from "@components/resale_submission/ArtistDetails";
import { ArtworkDetails } from "@components/resale_submission/ArtworkDetails";
import { ResaleDetails } from "@components/resale_submission/ResaleDetails";
import { useAuthenticationContext } from "@context/authentication";
import { AllRoles, Role } from "@roles";

import styles from "./DetailsDrawer.module.scss";

interface ResaleDetailsDrawerProps extends OffcanvasProps {
  resale: ResaleFragment;
  isDrawerOpen: boolean;
  refetch: () => void;
}

interface FormStatus {
  artist: { updated: boolean; valid: boolean };
  artwork: { updated: boolean; valid: boolean };
  resale: { updated: boolean; valid: boolean };
}

export const DetailsDrawer = ({ resale, isDrawerOpen, refetch, ...rest }: ResaleDetailsDrawerProps) => {
  const { auth } = useAuthenticationContext();
  const [isEditResale, setIsEditResale] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [note, setNote] = useState(resale?.resale_note || "");
  const [insertResalesNotesMutation, { loading }] = useInsertOrUpdateResaleNoteMutation();
  const [updateResalesMutation] = useInsertOrUpdateResalesBatchMutation({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState<ToastMessageType>();
  const [formStatus, setFormStatus] = useState<FormStatus>({
    artist: { updated: false, valid: true },
    artwork: { updated: false, valid: true },
    resale: { updated: false, valid: true },
  });

  const artistSubmitRef = useRef<HTMLButtonElement>(null);
  const artworkSubmitRef = useRef<HTMLButtonElement>(null);
  const resaleSubmitRef = useRef<HTMLButtonElement>(null);

  const handleValidationStatus = (formName: keyof FormStatus, isValid: boolean) => {
    setFormStatus((prevStatus) => ({
      ...prevStatus,
      [formName]: { ...prevStatus[formName], valid: isValid },
    }));
  };

  const transformToResaleInput = useCallback((resale: Resale | undefined): ResaleInput => {
    if (!resale) {
      return {} as ResaleInput;
    }
    return {
      resale_id: resale.resale_id,
      vendor_id: resale.vendor_id,
      opt_out_expiry: resale.opt_out_expiry,
      resale_status: resale.resale_status,
      royalty_payable: resale.royalty,
      artist: {
        artist_id: resale.artist_id,
        provisional_artist_country: isEmpty(resale.artist_country)
          ? resale.provisional_artist_country
          : resale.artist_country,
        provisional_is_artist_alive: resale.is_artist_living,
        provisional_artist_name: resale.artist_name,
        provisional_artist_yob:
          toNumber(isNil(resale.artist_year_of_birth) ? resale.provisional_artist_yob : resale.artist_year_of_birth) ||
          undefined,
        provisional_artist_yod:
          toNumber(isNil(resale.artist_year_of_death) ? resale.provisional_artist_yod : resale.artist_year_of_death) ||
          undefined,
      },
      artwork: {
        artwork_id: resale.artwork_id,
        description: resale.description,
        gallery_work_id: resale.gallery_work_id,
        height: resale.artwork_height,
        medium: resale.medium || "",
        artist_work_id: resale.artist_work_id,
        artwork_name: resale.artwork_name || "",
        width: resale.artwork_width,
      },
      batch_discriminator: resale.batch_grouping_id,
      resale: {
        acquired_date_choice: resale.acquired_date_choice || AcquiredDateChoices.NO,
        artwork_sale_date: resale.date_sold ? dayjs(resale.date_sold).format("YYYY-MM-DD") : "",
        artwork_sale_price: resale.resale_price || 0,
      },
    };
  }, []);

  const initialResaleInput = useMemo(() => transformToResaleInput(resale), [resale, transformToResaleInput]);
  const [resaleInput, setResaleInput] = useState(initialResaleInput);

  useEffect(() => {
    setNote(resale?.resale_note || "");
    setResaleInput(transformToResaleInput(resale));
  }, [resale, transformToResaleInput]);

  useEffect(() => {
    if (isDrawerOpen) {
      setIsEditResale(false);
    }
  }, [isDrawerOpen]);
  // Show toast message for 2 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleConfirmNoteClick = async () => {
    try {
      if (!resale) {
        setToastMessage("Error adding note. Please try again.");
        setToastMessageType("error");
        setShowToast(true);
        return;
      }
      const insertResalesNotesStatus = await insertResalesNotesMutation({
        variables: {
          resaleNotes: {
            resale_id: resale.resale_id,
            note,
            insert_new: true,
          },
        },
        refetchQueries: [ListAllNotesDocument],
      });
      setToastMessage(insertResalesNotesStatus.data?.insertOrUpdateResaleNote?.message || "Note added successfully");
      setToastMessageType("success");
      localStorage.setItem("openResaleId", resale.resale_id.toString());
      refetch();
    } catch {
      setToastMessage("Error adding note. Please try again.");
      setToastMessageType("error");
    } finally {
      setShowToast(true);
    }
  };

  const handleSaveEditClick = () => {
    if (!artistSubmitRef.current || !artworkSubmitRef.current || !resaleSubmitRef.current) {
      console.error("Error Reference Form Submit Buttons", artistSubmitRef, artworkSubmitRef, resaleSubmitRef);
    } else {
      try {
        setFormStatus({
          artist: { ...formStatus.artist, updated: false },
          artwork: { ...formStatus.artwork, updated: false },
          resale: { ...formStatus.resale, updated: false },
        });
        artistSubmitRef.current.click();
        artworkSubmitRef.current.click();
        resaleSubmitRef.current.click();
        setIsSubmitting(true);
      } catch (error) {
        console.error("Error submitting forms", error);
      }
    }
  };

  const isAllFormsUpdated = useCallback(() => {
    return formStatus.artist.updated && formStatus.artwork.updated && formStatus.resale.updated;
  }, [formStatus]);

  const isAllFormsValid = useCallback(() => {
    return formStatus.artist.valid && formStatus.artwork.valid && formStatus.resale.valid;
  }, [formStatus]);

  // Submit resale details after all forms are updated
  useEffect(() => {
    if (isSubmitting) {
      if (isAllFormsUpdated() && isAllFormsValid()) {
        updateResalesMutation({
          variables: {
            resales: [resaleInput],
          },
        })
          .then((response) => {
            if (!response.data?.insertOrUpdateResalesBatch) {
              throw new Error("Error inserting resales");
            }

            response.data.insertOrUpdateResalesBatch.resales.forEach((resale) => {
              if (!resale.success) {
                throw new Error("Error updating resales, resale data not updated");
              }
            });

            setToastMessage("Resale details updated successfully");
            setToastMessageType("success");
            setIsEditResale(false);
            refetch();
          })
          .catch((error) => {
            setToastMessage(`Error updating resale details: ${error.message}`);
            setToastMessageType("error");
          });
      } else if (!isAllFormsValid()) {
        setToastMessage("Save failed, please check the form.");
        setToastMessageType("error");
      }
      setIsSubmitting(false);
      setShowToast(true);
    }
  }, [isAllFormsUpdated, isAllFormsValid, isSubmitting, refetch, resaleInput, updateResalesMutation]);

  const updateResaleInput = (updatedInput: Partial<ResaleInput>) => {
    Object.keys(updatedInput).forEach((key) => {
      setFormStatus((prevStatus) => ({
        ...prevStatus,
        [key]: { ...prevStatus[key as keyof FormStatus], updated: true },
      }));
    });
    setResaleInput((prevInput) => ({
      ...prevInput,
      ...updatedInput,
    }));
  };
  return (
    <>
      <ToastMessage
        message={toastMessage}
        show={showToast}
        setShow={setShowToast}
        delay={2000}
        type={toastMessageType}
      />
      <Offcanvas show={isDrawerOpen} placement="end" {...rest} className={styles["offcanvas-container"]}>
        <Offcanvas.Header closeButton className={styles["drawer-button"]} />
        <Offcanvas.Header className="p-0">
          <Col>
            <Offcanvas.Title className={classNames(styles.title)}>
              <h3 className="mb-0">{resale?.artwork_name}</h3>
            </Offcanvas.Title>
          </Col>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0" style={{ scrollbarWidth: "none" }}>
          {!isEditResale ? (
            <>
              <Stack gap={4} direction="vertical">
                <ResaleStatus resale={resale} refetch={refetch} />
                {/* Permit: Payment badge should be visible to all user group */}
                <ProtectedComponent auth={auth} requiredGroups={AllRoles}>
                  <PaymentBadgeDrawer resale={resale} />
                </ProtectedComponent>
                <PaymentTrackerDrawer resale={resale} />
                <ArtistDetailsDrawer resale={resale} setIsEditResale={setIsEditResale} />
                <ArtworkDetailsDrawer resale={resale} />
                <ResaleDetailsDrawer resale={resale} />
                {/*Permit: VA admin, MS team*/}
                <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices]}>
                  <ReportingDetailsDrawer resale={resale} />
                </ProtectedComponent>
                <ProtectedComponent
                  auth={auth}
                  requiredGroups={[Role.VisualArtistAdmin, Role.ResaleReporter, Role.MemberServices, Role.Data]}
                >
                  <ResalePriceBreakdown resale={resale} />
                </ProtectedComponent>
                {/*Permit: VA admin, MS team and Royalty recipient are able to View Recipients breakdown*/}
                <ProtectedComponent
                  auth={auth}
                  requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.RoyaltyRecipient]}
                >
                  <RecipientsBreakdown resale={resale} />
                </ProtectedComponent>
                {/*Permit: VA admin, MS team and Data team are able to see notes, only VA can edit notes*/}
                <ProtectedComponent
                  auth={auth}
                  requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}
                >
                  <DetailsNote
                    resaleId={resale.resale_id}
                    setNote={setNote}
                    handleConfirmClick={handleConfirmNoteClick}
                    loading={loading}
                  />
                </ProtectedComponent>
                <ProtectedComponent
                  auth={auth}
                  requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}
                >
                  <ResaleStatusHistoryDrawer resaleId={resale.resale_id} />
                </ProtectedComponent>
              </Stack>
            </>
          ) : (
            //Permit: only VA admin able to Edit artist/artwork/resale details
            <ProtectedComponent auth={auth} requiredGroups={[Role.VisualArtistAdmin]}>
              <Form onSubmit={(e) => e.preventDefault()}>
                <ArtistDetails
                  initialFormData={resaleInput.artist}
                  multiFormData={resaleInput}
                  onSubmit={(data: ArtistResaleInput) => updateResaleInput({ artist: data })}
                  isInsertMode={false}
                  submitRef={artistSubmitRef}
                  onValidationStatus={(isValid) => handleValidationStatus("artist", isValid)}
                />
                <ArtworkDetails
                  initialFormData={resaleInput.artwork}
                  multiFormData={resaleInput}
                  onSubmit={(data: ArtworkInput) => updateResaleInput({ artwork: data })}
                  isInsertMode={false}
                  submitRef={artworkSubmitRef}
                  onValidationStatus={(isValid) => handleValidationStatus("artwork", isValid)}
                />
                <ResaleDetails
                  initialFormData={resaleInput.resale}
                  multiFormData={resaleInput}
                  onSubmit={(data: ResaleDataInput) => updateResaleInput({ resale: data })}
                  isInsertMode={false}
                  isEditable={resale.resale_status !== ResaleStatuses.RESEARCH_REQUIRED}
                  submitRef={resaleSubmitRef}
                  onValidationStatus={(isValid) => handleValidationStatus("resale", isValid)}
                />
                <Row>
                  <Col className="d-flex justify-content-center">
                    <button className="btn btn-primary mb-2 me-2 mt-4" type="button" onClick={handleSaveEditClick}>
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </Col>
                </Row>
              </Form>
            </ProtectedComponent>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};
